import SiteImage from "@/components/SiteImage";
import { BLOG_IMAGE_SIZES, optimizeArticleImages } from "@/lib/imageSources";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { usePublicUser as useUser } from "@/lib/publicAuth";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { formatPostDate } from "@/lib/publicDate";
import { useJsonLd, usePageMeta, canonicalUrl } from "@/lib/seo";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

interface BlogPostFull {
  handle: string;
  title: string;
  image: string | null;
  publishedAt: string;
  sourceUpdatedAt: string | null;
  author: string | null;
  contentHtml: string;
}

function prerenderedPost(handle: string): BlogPostFull | null {
  // Public build-time content only; the existing API still revalidates it.
  try {
    const json = document.getElementById("public-blog-post")?.textContent;
    const value = json ? JSON.parse(json) as BlogPostFull : null;
    return value?.handle === handle ? value : null;
  } catch { return null; }
}

export default function BlogPostPage({ params }: { params: { handle: string } }) {
  const [, setLocation] = useLocation();
  const { isSignedIn } = useUser();
  const initialPost = useMemo(() => prerenderedPost(params.handle), [params.handle]);
  const [post, setPost] = useState<BlogPostFull | null>(initialPost);
  usePageMeta(post ? {
    title: post.title,
    description: post.contentHtml.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160),
    path: `/blog/${post.handle}`,
  } : null);
  const [loading, setLoading] = useState(!initialPost);
  const [notFound, setNotFound] = useState(false);
  const safeContentHtml = useMemo(() => optimizeArticleImages(sanitizeHtml(post?.contentHtml ?? "")), [post?.contentHtml]);
  const articleJsonLd = post
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        url: canonicalUrl(`/blog/${post.handle}`),
        ...(post.image ? { image: post.image } : {}),
        ...(post.author ? { author: { "@type": "Person", name: post.author } } : {}),
        publisher: {
          "@type": "Organization",
          "@id": "https://www.reframeyouth.com/#organization",
          name: "Reframe Youth",
        },
        datePublished: post.publishedAt,
        ...(post.sourceUpdatedAt ? { dateModified: post.sourceUpdatedAt } : {}),
      }
    : null;
  useJsonLd("blog-posting", articleJsonLd);

  useEffect(() => {
    let cancelled = false;
    setLoading(!initialPost);
    setNotFound(false);
    setPost(initialPost);
    // The shared router handles scrolling, including client handle changes.
    async function load() {
      try {
        const res = await fetch(`${BASE}/api/blog/posts/${encodeURIComponent(params.handle)}`);
        if (!res.ok) throw new Error(String(res.status));
        const json = (await res.json()) as { post: BlogPostFull };
        if (!cancelled) {
          setPost(json.post);
        }
      } catch {
        if (!cancelled) { setPost(null); setNotFound(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [params.handle, initialPost]);

  return (
    <div className="min-h-[100dvh] bg-white font-sans selection:bg-primary/20">
      <SiteNav isSignedIn={!!isSignedIn} setLocation={setLocation} />

      <main className="max-w-[780px] mx-auto px-6 lg:px-0 py-[56px]">
        <Link href="/blog" className="inline-flex items-center gap-2 text-[13.5px] font-bold text-primary hover:gap-3 transition-all mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to the blog
        </Link>

        {loading && (
          <div className="animate-pulse">
            <div className="h-10 bg-slate-100 rounded w-4/5" />
            <div className="h-4 bg-slate-100 rounded w-1/3 mt-5" />
            <div className="aspect-[16/9] bg-slate-100 rounded-2xl mt-8" />
            <div className="space-y-3 mt-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-4 bg-slate-100 rounded" />
              ))}
            </div>
          </div>
        )}

        {notFound && !loading && (
          <div className="text-center py-24">
            <p className="text-lg font-bold text-[#1A1A1A]">We couldn't find that post.</p>
            <Button className="mt-6 bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[12.5px] tracking-[0.09em] uppercase px-6 py-[20px] rounded-[10px]" asChild>
              <Link href="/blog">Browse all posts</Link>
            </Button>
          </div>
        )}

        {post && !loading && (
          <article>
            <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-primary mb-4">Truth in the Trenches</p>
            <h1 className="text-[clamp(30px,4.5vw,46px)] font-black text-[#1A1A1A] leading-[1.05] tracking-[-0.01em]">
              {post.title}
            </h1>
            <p className="mt-5 text-[14px] text-[#333333]/[0.67] font-medium">
              {post.author && <>By {post.author} · </>}
              {formatPostDate(post.publishedAt)}
            </p>

            {post.image && (
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden shadow-xl mt-9">
                <SiteImage src={post.image} alt={post.title} sizes={BLOG_IMAGE_SIZES} loading="eager" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            )}

            <div
              className="prose prose-slate max-w-none mt-10 prose-headings:font-black prose-headings:text-[#1A1A1A] prose-a:text-primary prose-a:font-semibold prose-img:rounded-xl prose-strong:text-[#1A1A1A] text-[16.5px] leading-[1.75]"
              dangerouslySetInnerHTML={{ __html: safeContentHtml }}
            />

            {/* CTA */}
            <div className="mt-16 rounded-2xl bg-[#1A1A1A] text-white p-9 lg:p-12 text-center">
              <h2 className="text-[clamp(22px,3vw,30px)] font-black leading-tight">
                Curriculum your students <span className="text-primary">won't shrug off</span>
              </h2>
              <p className="mt-3 text-white/60 text-[15px] max-w-[46ch] mx-auto leading-relaxed">
                Conversation-driven series where no topic is off-limits, relevant to culture, faithful to Scripture.
              </p>
              <Button className="mt-7 bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[12.5px] tracking-[0.09em] uppercase px-7 py-[22px] rounded-[10px]" asChild>
                <Link href="/shop">
                  Browse Curriculum <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
            </div>
          </article>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
